import { itemsApi } from "#/api/itemsApi";
import ActionButton from "#/components/buttons/ActionButton";
import { showSuccessNotification } from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { Item } from "#/types/items";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { describeItem } from "#/utils/itemLabel";
import { logger } from "#/utils/logger";
import { Accordion, Badge, Loader, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconExternalLink, IconPlus, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AddRelatedItemsModal from "./AddRelatedItemsModal";

interface RelatedItemsSectionProps {
  /** Saved item the links belong to, or null while the item is being created. */
  itemId: number | null;
  /** Links chosen before the item exists — the page writes them after saving. */
  pendingItems: Item[];
  onPendingItemsChange: (items: Item[]) => void;
}

/**
 * "Related Items" accordion on the item detail page. Links are symmetric, so
 * whatever is linked here also lists this item on its own page.
 *
 * On a saved item, adding and removing write immediately — this section is
 * independent of the item form. While creating, there is no id to link to yet,
 * so picks are held in the parent and written once the item is saved.
 */
const RelatedItemsSection = ({
  itemId,
  pendingItems,
  onPendingItemsChange,
}: RelatedItemsSectionProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const queryClient = useQueryClient();
  const [modalOpened, { open, close }] = useDisclosure(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const isSaved = itemId !== null;
  const queryKey = ["related-items", itemId];

  const { data: savedItems = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await itemsApi.getRelatedItems(itemId!);
      return response.data;
    },
    enabled: isSaved,
    staleTime: 1000 * 30,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: ["available-related-items", itemId] });
  };

  const unlinkMutation = useMutation({
    mutationFn: async (relatedId: number) => itemsApi.unlinkRelatedItem(itemId!, relatedId),
    onSuccess: () => {
      showSuccessNotification({ message: t("items.relatedItems.unlinked") });
      refresh();
    },
    onError: (error) => {
      logger.error("Failed to unlink related item:", error);
    },
    onSettled: () => setRemovingId(null),
  });

  const items = isSaved ? savedItems : pendingItems;

  const handleRemove = (item: Item) => {
    if (!isSaved) {
      onPendingItemsChange(pendingItems.filter((pending) => pending.id !== item.id));
      return;
    }
    setRemovingId(item.id);
    unlinkMutation.mutate(item.id);
  };

  const renderRow = (item: Item) => (
    <div
      key={item.id}
      className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => window.open(`/inventory/items/${item.id}`, "_blank")}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm font-semibold truncate">{describeItem(item)}</span>
        {item.status && (
          <Badge size="xs" variant="light" color="gray">
            {sanitizeItemStatusLabel(String(item.status))}
          </Badge>
        )}
        {item.product?.name && (
          <Text size="xs" c="dimmed" className="truncate">
            {item.product.name}
          </Text>
        )}
        <IconExternalLink size={12} className="opacity-40 shrink-0" />
      </div>

      <PermissionTooltip disabled={!canWrite()}>
        <ActionButton
          color="red"
          variant="subtle"
          size="xs"
          icon={IconX}
          disabled={!canWrite() || removingId === item.id}
          loading={removingId === item.id}
          onClick={(event) => {
            event.stopPropagation();
            handleRemove(item);
          }}
        />
      </PermissionTooltip>
    </div>
  );

  return (
    <Accordion.Item value="related_items">
      <Accordion.Control>
        <div className="flex items-center gap-2">
          <span>{t("items.relatedItems.title")}</span>
          {items.length > 0 && (
            <Badge size="xs" variant="light" color="gray">
              {items.length}
            </Badge>
          )}
          <PermissionTooltip disabled={!canWrite()}>
            <ActionButton
              color="black"
              size="xs"
              icon={IconPlus}
              disabled={!canWrite()}
              onClick={(event) => {
                // Without this the click would toggle the accordion instead.
                event.stopPropagation();
                open();
              }}
            />
          </PermissionTooltip>
        </div>
      </Accordion.Control>

      <Accordion.Panel>
        {!isSaved && (
          <Text size="xs" c="dimmed" className="mb-2 block">
            {t("items.relatedItems.pendingHint")}
          </Text>
        )}

        {isSaved && isLoading ? (
          <Loader size="sm" />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("items.relatedItems.empty")}
          </Text>
        ) : (
          <div className="flex flex-col gap-2">{items.map(renderRow)}</div>
        )}
      </Accordion.Panel>

      <AddRelatedItemsModal
        opened={modalOpened}
        onClose={close}
        itemId={itemId}
        excludedIds={isSaved ? [] : pendingItems.map((item) => item.id)}
        onLinked={refresh}
        onStaged={(picked) => {
          const existing = new Set(pendingItems.map((item) => item.id));
          onPendingItemsChange([
            ...pendingItems,
            ...picked.filter((item) => !existing.has(item.id)),
          ]);
        }}
      />
    </Accordion.Item>
  );
};

export default RelatedItemsSection;
